import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle } from "lucide-react";
import DiagnosticoUtilidadPanel from "@/components/DiagnosticoUtilidadPanel";
import AuditoriaLotesPanel from "@/components/AuditoriaLotesPanel";
import CostoCeroPanel from "@/components/CostoCeroPanel";
import AuditoriaCostosPanel from "@/components/AuditoriaCostosPanel";
import { CostoRevisadoLista } from "@/components/CostoRevisado";

const cop = (n) => `$ ${Math.round(n || 0).toLocaleString("es-CO")}`;

/**
 * Jun/2026: UN solo panel "Revisión de costos" en Finanzas. Hace un chequeo rápido y
 * solo muestra la herramienta correspondiente cuando hay algo que corregir.
 * El análisis detallado por contenedor queda bajo "Ver análisis detallado".
 */
export default function RevisionCostosPanel({ desde, hasta, modo, onRecalculado }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detalle, setDetalle] = useState(false);
  const [reparando, setReparando] = useState(false);

  const cargar = () => {
    setLoading(true);
    Promise.all([
      api.get("/productos/_/desglose/reparar-historico/preview").then((r) => r.data).catch(() => null),
      api.get("/reportes/auditoria-costo-cero").then((r) => r.data).catch(() => null),
      api.get("/reportes/auditoria-lotes").then((r) => r.data).catch(() => null),
    ]).then(([rep, cero, infl]) => setStatus({ rep, cero, infl })).finally(() => setLoading(false));
  };
  useEffect(() => { if (open && !status) cargar(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const reparar = async () => {
    const r0 = status.rep;
    if (!window.confirm(`Se corregirán ${r0.movimientos} despacho(s) con costo de paquete por unidad (costo bajará ${cop(r0.diferencia)}). Queda registro en Auditoría. ¿Continuar?`)) return;
    setReparando(true);
    try {
      const { data } = await api.post("/productos/_/desglose/reparar-historico");
      toast.success(`Listo: ${data.movimientos} despachos corregidos · ${cop(data.diferencia)} menos de costo`);
      setStatus(null); cargar(); onRecalculado?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo reparar"); } finally { setReparando(false); }
  };

  const nRep = status?.rep?.movimientos || 0;
  const nCero = status?.cero?.productos_afectados || 0;
  const nInfl = status?.infl?.productos_afectados || 0;
  const todoOk = status && nRep === 0 && nCero === 0 && nInfl === 0;

  return (
    <div className="bg-card border border-border rounded-xl p-4 mb-6" data-testid="revision-costos-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="revision-costos-toggle">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" />
          <div>
            <div className="text-sm font-semibold text-foreground">Revisión de costos</div>
            <div className="text-xs text-muted-foreground">Chequeo automático del costo de mercancía. Solo aparece algo si hay que corregir.</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          {loading && <div className="text-sm text-muted-foreground">Revisando despachos y lotes…</div>}
          {status && (
            <>
              <ul className="text-xs space-y-1.5" data-testid="revision-costos-checklist">
                <Check ok={nRep === 0} okText="Despachos con costo de paquete tras desglose: ninguno" badText={`${nRep} despacho(s) quedaron con costo de paquete por unidad (costo inflado ${cop(status.rep?.diferencia)})`}>
                  {nRep > 0 && <button type="button" disabled={reparando} onClick={reparar} className="ml-2 h-7 px-3 rounded-md bg-rose-600 text-white text-[11px] font-semibold disabled:opacity-50" data-testid="revision-reparar-btn">{reparando ? "Corrigiendo…" : "Corregir ahora"}</button>}
                </Check>
                <Check ok={nCero === 0} okText="Lotes o despachos a costo $0: ninguno" badText={`${nCero} producto(s) con lotes/despachos a costo $0 (utilidad sobreestimada ${cop(status.cero?.cogs_faltante_total)})`} />
                <Check ok={nInfl === 0} okText="Lotes con costo inflado vs. ficha: ninguno" badText={`${nInfl} producto(s) con lotes cuyo costo es muy superior al de la ficha`} />
              </ul>
              {todoOk && <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="revision-costos-ok"><CheckCircle2 className="w-4 h-4" /> Costos en orden. La utilidad que ves en Finanzas es confiable.</div>}

              {nCero > 0 && <CostoCeroPanel onCorregido={() => { setStatus(null); cargar(); onRecalculado?.(); }} />}
              {nInfl > 0 && <AuditoriaLotesPanel />}
              {nInfl === 0 && <CostoRevisadoLista total={status.infl?.revisados} onCambio={() => { setStatus(null); cargar(); }} />}

              <button type="button" onClick={() => setDetalle((d) => !d)} className="text-xs text-primary underline" data-testid="revision-costos-detalle-toggle">
                {detalle ? "Ocultar análisis detallado" : "Ver análisis detallado (por contenedor, márgenes, auditoría de costos)"}
              </button>
              {detalle && (
                <>
                  <DiagnosticoUtilidadPanel desde={desde} hasta={hasta} />
                  <AuditoriaCostosPanel desde={desde} hasta={hasta} modo={modo} onRecalculado={onRecalculado} />
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Check({ ok, okText, badText, children }) {
  return (
    <li className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 ${ok ? "bg-emerald-50/60 text-emerald-800 dark:bg-emerald-900/15" : "bg-rose-50/70 text-rose-800 border border-rose-200 dark:bg-rose-900/15"}`}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />}
      <span className="flex-1">{ok ? okText : badText}</span>
      {children}
    </li>
  );
}
